// components/LookThreeDotMenu.tsx
import React, { useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import AnimationIcon from "@mui/icons-material/Animation";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import { CustomPhotoAvatar } from "../../../../context/types";

interface LookThreeDotMenuProps {
  look: CustomPhotoAvatar;
  onOpenAddMotion?: (look: CustomPhotoAvatar) => void;
  onAddSoundEffect?: (look: CustomPhotoAvatar) => void;
  onDeleteLook?: (lookId: string) => void;
  isAddingMotion?: boolean;
  isAddingSoundEffect?: boolean;
}

const LookThreeDotMenu: React.FC<LookThreeDotMenuProps> = ({
  look,
  onOpenAddMotion,
  onAddSoundEffect,
  onDeleteLook,
  isAddingMotion = false,
  isAddingSoundEffect = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Check if look can have motion added (must be successful and not already have motion)
  const canAddMotion = look.status === "success" && !look.is_motion;

  // Check if look can have sound effect added (must have motion first and not already have sound effect)
  const canAddSoundEffect = look.status === "success" && look.is_motion === true && !look.background_sound_effect;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    event.nativeEvent.stopImmediatePropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddMotionClick = () => {
    onOpenAddMotion?.(look);
    handleMenuClose();
  };

  const handleAddSoundEffectClick = () => {
    onAddSoundEffect?.(look);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    onDeleteLook?.(look._id);
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Only show menu if there are actionable items
  const hasActions = canAddMotion || canAddSoundEffect || onDeleteLook;
  if (!hasActions) return null;

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
      }}
      style={{ display: "inline-block" }}
    >
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 1)",
          },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.nativeEvent.stopImmediatePropagation();
        }}
      >
        {canAddMotion && onOpenAddMotion && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              handleAddMotionClick();
            }}
            disabled={isAddingMotion}
          >
            <ListItemIcon>
              <AnimationIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {isAddingMotion ? "Adding Motion..." : "Add Motion"}
            </ListItemText>
          </MenuItem>
        )}
        {canAddSoundEffect && onAddSoundEffect && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              handleAddSoundEffectClick();
            }}
            disabled={isAddingSoundEffect}
          >
            <ListItemIcon>
              <MusicNoteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {isAddingSoundEffect ? "Adding Sound Effect..." : "Add Sound Effect"}
            </ListItemText>
          </MenuItem>
        )}
        {onDeleteLook && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              handleDeleteClick();
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Look</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={(e: any) => {
          e?.stopPropagation();
          e?.preventDefault();
          handleDeleteCancel();
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.nativeEvent.stopImmediatePropagation();
        }}
      >
        <DialogTitle>Delete Look</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{look.name}"? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              handleDeleteCancel();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              handleDeleteConfirm();
            }}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LookThreeDotMenu;
